import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Loading from "../Components/Loading";
import Error from "../Components/Error";

const AddComplaintForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Complaint Types
  const [complaintTypes, setComplaintTypes] = useState([]);

  // Form State
  const [formData, setFormData] = useState({
    complaintTypeId: "",
    farmerName: "",
    farmerVillageName: "",
    farmerAddress: "",
    farmerNumber: "",
    farmGatNumberSurveyNumberDetails: "",
    farmLatitude: "",
    farmLongitude: "",
    taluka: "",
    district: "",
    state: "",
    locationLink: "",
    complaintDetails1: "",
    complaintDetails2: "",
    sowingDate: "",
    issueSpottedDate: "",
    issueReportedDate: "",
    status: "Pending",
    resolutionDetails: "",
  });

  // Documents state
  const [documents, setDocuments] = useState(
    Array.from({ length: 10 }, () => ({
      file: null,
      details: "",
      comment: "",
    }))
  );

  const distributorId = localStorage.getItem("distributorId");
  const distributorName = localStorage.getItem("distributorName");
  const distributorForOrgs = localStorage.getItem("distributorForOrgs");

  useEffect(() => {
    fetchComplaintTypes();
  }, []);

  // Fetch complaint types from API
  const fetchComplaintTypes = async () => {
    try {
      const token =
        localStorage.getItem("loginToken") || localStorage.getItem("token");
      const BASE_URL = "https://dpapi.boosterparis.in/";

      const response = await axios.get(
        `${BASE_URL}api/Complaint/GetComplaintTypes/GetComplaintTypes`,
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      );

      // Your API returns {result: array, isSuccess: boolean}
      if (response.data && response.data.isSuccess) {
        // Map the API response to match our expected format
        const mappedTypes = response.data.result.map((item) => ({
          Id: item.id,
          Name: item.name,
          TypeName: item.name,
        }));

        setComplaintTypes(mappedTypes);
      } else {
        setError("Failed to load complaint types");
      }
    } catch (err) {
      console.error("Error fetching complaint types:", err);
      setError("Error loading complaint types from server");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type } = e.target;

    // Handle different input types
    const processedValue =
      type === "number" ? (value === "" ? "" : parseFloat(value)) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));
  };

  const handleTextareaChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDocumentChange = (index, field, value) => {
    const updatedDocuments = [...documents];
    updatedDocuments[index][field] = value;
    setDocuments(updatedDocuments);
  };

  const handleFileChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("File size should be less than 5MB");
        e.target.value = "";
        return;
      }

      // Check file type
      const allowedTypes = [
        "image/jpeg",
        "image/png",
        "image/jpg",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        alert("Only JPEG, PNG, JPG, and PDF files are allowed");
        e.target.value = "";
        return;
      }

      handleDocumentChange(index, "file", file);
    }
  };

  // Form validation
  const validateForm = () => {
    if (!formData.complaintTypeId) {
      alert("Please select complaint type");
      return false;
    }

    if (!formData.farmerName.trim()) {
      alert("Please enter farmer name");
      return false;
    }

    // Validate mobile number if provided
    if (formData.farmerNumber && !/^\d{10}$/.test(formData.farmerNumber)) {
      alert("Please enter a valid 10-digit mobile number");
      return false;
    }

    return true;
  };

  // Add distributorId to form data
  const setBasicDistId = () => {
    formData.distributorId = distributorId;
    formData.createdById = localStorage.getItem("userId");
  };

  // Submit complaint
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Prepare form data
      const token = localStorage.getItem("loginToken");
      const BASE_URL = "https://dpapi.boosterparis.in/";

      const formDataToSend = new FormData();

      // Add basic form fields
      Object.keys(formData).forEach((key) => {
        if (
          formData[key] !== null &&
          formData[key] !== undefined &&
          formData[key] !== ""
        ) {
          formDataToSend.append(key, formData[key]);
        }
      });

      // Add system fields
      formDataToSend.append("DistributorId", distributorId);
      formDataToSend.append(
        "CreatedById",
        localStorage.getItem("userId") || ""
      );

      // Add documents
      documents.forEach((doc, index) => {
        if (doc.file) {
          formDataToSend.append("Files", doc.file);
        }
      });

      // Add document details as simple arrays instead of JSON
      const docDetails = documents
        .filter((doc) => doc.file)
        .map((doc) => doc.details || "");
      const docComments = documents
        .filter((doc) => doc.file)
        .map((doc) => doc.comment || "");

      docDetails.forEach((detail, index) => {
        formDataToSend.append(`DocumentDetails[${index}]`, detail);
      });

      docComments.forEach((comment, index) => {
        formDataToSend.append(`DocumentComments[${index}]`, comment);
      });

      for (let pair of formDataToSend.entries()) {
        console.log(pair[0], pair[1]);
      }

      // Submit to API
      const response = await axios.post(
        `${BASE_URL}api/Complaint/SubmitComplaint/SubmitComplaint`,
        formDataToSend,
        {
          headers: {
            Authorization: token,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      // Check for success in multiple ways
      if (response.status >= 200 && response.status < 300) {
        // Check user type और उसके according navigate करें
        const userType = localStorage.getItem("userType");
        console.log(userType);

        if (userType === "Distributor") {
          // Distributor होने पर distributor dashboard पर return
          navigate("/"); // या आपका distributor dashboard route
        } else {
          // Sales officer/BPC user होने पर उनके dashboard पर
          navigate("/BPUDashboard/BPUDashContent");
        }
      }
      // ... rest of the code ...
    } catch (err) {
      console.error("Submit error:", err);
      setError(
        err.response?.data?.message ||
        err.message ||
        "Error submitting complaint"
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error data={error} />;
  }

  return (
    <section className="content">
      <div className="container-fluid">
        <br />
        <div className="row">
          <div className="col-12">
            <div className="card card-success">
              <div className="card-header">
                <h3 className="card-title">
                  Add Complaint - {distributorName}
                  {distributorForOrgs && ` [${distributorForOrgs}]`}
                </h3>
              </div>

              <div className="card-body addPartFC">
                {/* Distributor Information - Read Only */}
                <div className="card card-secondary mb-4">
                  <div className="card-header">
                    <h4 className="card-title">Distributor Information</h4>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-sm-4">
                        <div className="form-group">
                          <label>Distributor Name</label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={distributorName || "N/A"}
                            readOnly
                          />
                        </div>
                      </div>
                      {/* <div className="col-sm-4">
                        <div className="form-group">
                          <label>Distributor ID</label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={distributorId || "N/A"}
                            readOnly
                          />
                        </div>
                      </div> */}
                      <div className="col-sm-4">
                        <div className="form-group">
                          <label>Organization</label>
                          <input
                            type="text"
                            className="form-control bg-light"
                            value={distributorForOrgs || "N/A"}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <form
                  onSubmit={handleSubmit}
                  className="master-form"
                  noValidate
                >
                  {/* Complaint Type & Farmer Details */}
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label>Complaint Type *</label>
                        <select
                          name="complaintTypeId"
                          value={formData.complaintTypeId}
                          onChange={handleChange}
                          className="form-control"
                        >
                          <option value="">-- Select Complaint Type --</option>
                          {complaintTypes.map((type) => (
                            <option key={type.Id} value={type.Id}>
                              {type.Name || type.TypeName}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <div className="form-group">
                        <label>Farmer Name *</label>
                        <input
                          type="text"
                          name="farmerName"
                          value={formData.farmerName}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Enter Farmer Name"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label>Farmer Mobile Number</label>
                        <input
                          type="text"
                          name="farmerNumber"
                          value={formData.farmerNumber}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="10 digit mobile number"
                          maxLength="10"
                        />
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <div className="form-group">
                        <label>Village Name</label>
                        <input
                          type="text"
                          name="farmerVillageName"
                          value={formData.farmerVillageName}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Enter Village Name"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-sm-12">
                      <div className="form-group">
                        <label>Farmer Address</label>
                        <textarea
                          name="farmerAddress"
                          value={formData.farmerAddress}
                          onChange={handleTextareaChange}
                          className="form-control"
                          rows="2"
                          placeholder="Enter Complete Address"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Farm Details */}
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label>Farm Gat/Survey Number Details</label>
                        <input
                          type="text"
                          name="farmGatNumberSurveyNumberDetails"
                          value={formData.farmGatNumberSurveyNumberDetails}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Gat number, Survey number, etc."
                        />
                      </div>
                    </div>

                    <div className="col-sm-3">
                      <div className="form-group">
                        <label>Farm Latitude</label>
                        <input
                          type="number"
                          name="farmLatitude"
                          value={formData.farmLatitude}
                          onChange={handleChange}
                          className="form-control"
                          step="any"
                          placeholder="e.g., 19.0760"
                        />
                      </div>
                    </div>

                    <div className="col-sm-3">
                      <div className="form-group">
                        <label>Farm Longitude</label>
                        <input
                          type="number"
                          name="farmLongitude"
                          value={formData.farmLongitude}
                          onChange={handleChange}
                          className="form-control"
                          step="any"
                          placeholder="e.g., 72.8777"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Location Details */}
                  <div className="row">
                    <div className="col-sm-4">
                      <div className="form-group">
                        <label>Taluka</label>
                        <input
                          type="text"
                          name="taluka"
                          value={formData.taluka}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Enter Taluka"
                        />
                      </div>
                    </div>

                    <div className="col-sm-4">
                      <div className="form-group">
                        <label>District</label>
                        <input
                          type="text"
                          name="district"
                          value={formData.district}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Enter District"
                        />
                      </div>
                    </div>

                    <div className="col-sm-4">
                      <div className="form-group">
                        <label>State</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="Enter State"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-sm-12">
                      <div className="form-group">
                        <label>Google Maps Location Link</label>
                        <input
                          type="text"
                          name="locationLink"
                          value={formData.locationLink}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="https://maps.google.com/?q=lat,lng"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Complaint Details */}
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="form-group">
                        <label>Complaint Details *</label>
                        <textarea
                          name="complaintDetails1"
                          value={formData.complaintDetails1}
                          onChange={handleTextareaChange}
                          className="form-control"
                          rows="4"
                          placeholder="Describe the problem in detail"
                          required
                        />
                      </div>
                    </div>

                    <div className="col-sm-6">
                      <div className="form-group">
                        <label>Additional Details</label>
                        <textarea
                          name="complaintDetails2"
                          value={formData.complaintDetails2}
                          onChange={handleTextareaChange}
                          className="form-control"
                          rows="4"
                          placeholder="Any additional information"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="row">
                    <div className="col-sm-4">
                      <div className="form-group">
                        <label>Sowing Date</label>
                        <input
                          type="date"
                          name="sowingDate"
                          value={formData.sowingDate}
                          onChange={handleDateChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="col-sm-4">
                      <div className="form-group">
                        <label>Issue Spotted Date</label>
                        <input
                          type="date"
                          name="issueSpottedDate"
                          value={formData.issueSpottedDate}
                          onChange={handleDateChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div className="col-sm-4">
                      <div className="form-group">
                        <label>Issue Reported Date</label>
                        <input
                          type="date"
                          name="issueReportedDate"
                          value={formData.issueReportedDate}
                          onChange={handleDateChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Document Upload Section */}
                  <div className="row mt-4">
                    <div className="col-12">
                      <div className="card card-info">
                        <div className="card-header">
                          <h4 className="card-title">
                            Document Upload (Max 10 files, 5MB each, JPG/PNG/PDF
                            only)
                          </h4>
                        </div>
                        <div className="card-body">
                          <div className="row">
                            {documents.map((doc, index) => (
                              <div
                                key={index}
                                className="col-sm-6 col-md-4 mb-3"
                              >
                                <div className="card card-outline">
                                  <div className="card-header">
                                    <h5 className="card-title mb-0">
                                      Document {index + 1}
                                    </h5>
                                  </div>
                                  <div className="card-body">
                                    <div className="form-group">
                                      <label>File</label>
                                      <input
                                        type="file"
                                        className="form-control"
                                        onChange={(e) =>
                                          handleFileChange(index, e)
                                        }
                                        accept=".jpg,.jpeg,.png,.pdf"
                                      />
                                      {doc.file && (
                                        <small className="text-success">
                                          Selected: {doc.file.name}
                                        </small>
                                      )}
                                    </div>

                                    <div className="form-group">
                                      <label>Document Details</label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={doc.details}
                                        onChange={(e) =>
                                          handleDocumentChange(
                                            index,
                                            "details",
                                            e.target.value
                                          )
                                        }
                                        placeholder="Document description"
                                      />
                                    </div>

                                    <div className="form-group">
                                      <label>Comments</label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={doc.comment}
                                        onChange={(e) =>
                                          handleDocumentChange(
                                            index,
                                            "comment",
                                            e.target.value
                                          )
                                        }
                                        placeholder="Any comments"
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="row">
                    <div className="col-sm-12">
                      <div className="form-group">
                        {error && (
                          <div className="alert alert-danger">
                            <strong>Error!</strong> {error}
                          </div>
                        )}

                        {/* <button
                          type="button"
                          className="btn btn-default mr-2"
                          onClick={() => navigate(-1)}
                          disabled={submitting}
                        >
                          Cancel
                        </button> */}

                        <input
                          type="submit"
                          className="btn btn-success float-right submit-btn"
                          value={
                            submitting ? "Submitting..." : "Submit Complaint"
                          }
                          disabled={submitting || complaintTypes.length === 0}
                        />
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AddComplaintForm;
